import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiOrder, type ApiRequisition, type EnumContainerActionRequest } from '@Model';
import { ContainerActionsRepository, RequisitionContainerActionsRepository } from '@Repositories/Auftragsbearbeitung';
import { type AsyncStateUpdate, isOk, type Result } from '@synqo/frontend-component-library';
import { useCallback, useRef, useState } from 'react';
import { type ContainerPlanSource, toPlanRequest } from './containerPlanSource';

/** The draft the planned container actions are added to, and where its state is kept. */
export type ContainerPlanTarget =
    | { kind: 'order'; id: string; requestUpdateState: AsyncStateUpdate<ApiOrder> }
    | { kind: 'requisition'; id: string; requestUpdateState: AsyncStateUpdate<ApiRequisition> };

export interface ContainerPlanning {
    /**
     * Has the server plan a container action, or an exchange of two, from a source if there is one, and takes the
     * returned draft into state. Does nothing while another call is in flight. Resolves to whether the draft was
     * planned; the server's refusal reaches the user through the frame's messages.
     */
    plan: (
        action: EnumContainerActionRequest | undefined,
        source?: ContainerPlanSource,
        replacementContainerTypeId?: string,
    ) => Promise<boolean>;
    /** Whether a planning call is in flight; a second one would only wait for the draft's lock. */
    isPlanning: boolean;
}

const inState = <TDraft>(planned: Result<TDraft>) =>
    isOk(planned)
        ? { data: planned.data, messages: planned.messages }
        : { data: undefined, messages: planned.messages };

/** The container actions the server plans on an order or a requisition that is open. */
// @ai-generated 2026-10-09T21:30:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useContainerPlanning(target: ContainerPlanTarget): ContainerPlanning {
    const orderActions = useAuftragRepository(ContainerActionsRepository);
    const requisitionActions = useAuftragRepository(RequisitionContainerActionsRepository);

    const [isPlanning, setIsPlanning] = useState(false);
    const inFlight = useRef(false);

    const plan = useCallback(
        async (
            action: EnumContainerActionRequest | undefined,
            source?: ContainerPlanSource,
            replacementContainerTypeId?: string,
        ) => {
            if (inFlight.current) {
                return false;
            }

            inFlight.current = true;
            setIsPlanning(true);
            try {
                const request = toPlanRequest(action, source, replacementContainerTypeId);

                if (target.kind === 'order') {
                    return await target.requestUpdateState(async () =>
                        inState(await orderActions.plan(request, target.id)),
                    );
                }

                return await target.requestUpdateState(async () =>
                    inState(await requisitionActions.plan(request, target.id)),
                );
            } finally {
                inFlight.current = false;
                setIsPlanning(false);
            }
        },
        [target, orderActions, requisitionActions],
    );

    return { plan, isPlanning };
}
